/**
 * Vorlage „Kneten“.
 *
 * Eine Teigkugel liegt auf der bemehlten Arbeitsfläche. Der Handballen drückt
 * den Teig nach vorn flach, dann wird er zurückgeklappt und wieder rund.
 * Zwei Durchgänge je Schleife.
 */
import { ease, fract, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, compact, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { doughBall } from '../parts/food.ts';
import { hand } from '../parts/hand.ts';
import { arrow, counter, BADGE_RIGHT, durationLabel, GROUND_Y, shadow, timerBadge } from '../parts/stage.ts';
import { place } from '../parts/utensils.ts';
import { minutesFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Teig in Ruhe. */
const DOUGH = { cx: 100, width: 78, height: 44 } as const;

/** Mehlstaub auf der Arbeitsfläche. */
const FLOUR: readonly (readonly [number, number])[] = [
  [40, 2],
  [52, 6],
  [150, 3],
  [164, 7],
  [176, 2],
  [62, 10],
  [140, 12],
  [30, 9],
];

/** Vorlage „Kneten“. */
export const mixKneadTemplate = defineTemplate({
  id: 'mix.knead',
  group: 'mix',
  title: 'Kneten',
  description:
    'Der Teig wird mit dem Handballen nach vorn gedrückt, zurückgeklappt und wieder geformt – so lange, bis er glatt und elastisch ist.',
  durationSeconds: 5,
  keywords: ['kneten', 'durchkneten', 'teig kneten', 'glatten teig', 'geschmeidig', 'elastisch'],
  stillTime: 0.2,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 1,
      max: 30,
      step: 1,
      unit: 'Min',
      defaultValue: 10,
    },
  ],
  inferValues(text) {
    return { minutes: minutesFromText(text) };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 10);
    const local = fract(t * 2);
    // Drücken (flach und nach vorn), dann Zurückklappen (wieder rund).
    const push = ease.inOutSine(segment(local, 0.12, 0.42)) - ease.inOutSine(segment(local, 0.62, 0.88));
    const fold = Math.sin(segment(local, 0.62, 0.88) * Math.PI);
    const width = lerp(DOUGH.width, DOUGH.width * 1.42, push);
    const height = lerp(DOUGH.height, DOUGH.height * 0.62, push) + fold * 6;
    const cx = DOUGH.cx + push * 16;
    const outline = doughBall(cx, GROUND_Y, width, height);
    const crease =
      fold > 0.05
        ? shape(
            path()
              .move(cx - width * 0.3, GROUND_Y - height * 0.55)
              .quad(cx, GROUND_Y - height * (0.55 + 0.3 * fold), cx + width * 0.32, GROUND_Y - height * 0.5)
              .toString(),
            { stroke: 'cavity', strokeWidth: STROKE.detail },
          )
        : null;

    // Hand: senkt sich auf den Teig, schiebt mit dem Ballen nach vorn, hebt ab.
    const down = ease.inOutSine(segment(local, 0, 0.12)) - ease.inOutSine(segment(local, 0.42, 0.56));
    const handX = cx - width * 0.3 + push * 10;
    const top = GROUND_Y - height;
    const handY = lerp(top - 16, top + 4 + push * 3, down);
    const handTurn = lerp(-8, 10, down);
    const dust: SceneNode[] = FLOUR.map(([x, dy]) => circle(x, GROUND_Y + dy, 1.4, styles.flat('shade')));
    return compact([
      ...counter(),
      ...dust,
      shadow(cx, width * 0.5),
      shape(outline, styles.snow(STROKE.outline)),
      crease,
      place(handX, handY, handTurn, hand(), 1.15),
      timerBadge(BADGE_RIGHT, 24, durationLabel(minutes), t),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 70, y: 74 },
        { x: 110, y: 72 },
        { x: 148, y: 76 },
      ]),
    ];
  },
});
